/* eslint-disable camelcase */
import { useContext } from 'react';

import { useQuery } from '@apollo/client';
import type { ApolloError } from '@apollo/client';

import { normalizeCollection } from 'bundles/campaigns/hooks/useCollection';
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import { getLocalizedQueryVariables } from 'bundles/internationalization-lib/components/WithLocalizedContent';
import type { PromoTemplatePageContentModulesCollection } from 'bundles/promo-template/__generated__/baseContentfulTypes';
import {
  PromoTemplatePageContentSegmentOneFragmentDoc,
  PromoTemplatePageContentSegmentTwoFragmentDoc,
} from 'bundles/promo-template/__generated__/contentfulOperations';
import type {
  PromoTemplateArticlesCollectionFragment,
  PromoTemplateCannedCollectionFragment,
  PromoTemplateCitationFragment,
  PromoTemplateCollectionsFragment,
  PromoTemplateFaqsFragment,
  PromoTemplateHeroFragment,
  PromoTemplateInformationFragment,
  PromoTemplatePageContentSegmentOneFragment,
  PromoTemplatePageContentSegmentTwoFragment,
  PromoTemplatePartnerListFragment,
  PromoTemplatePromoBarFragment,
  PromoTemplateTestimonialCarouselFragment,
  PromoTemplateValuePropsFragment,
} from 'bundles/promo-template/__generated__/contentfulTypes';
import { DOMAINS } from 'bundles/promo-template/constants/navigation';
import { PromoTemplateContext } from 'bundles/promo-template/contexts/PromoTemplateContext';
import type {
  PromoTemplateCollection_CmsProxyBffFragment as BffCollectionFragment,
  GetConsumerVariantContentQuery,
} from 'bundles/promo-template/queries/__generated__/getConsumerVariantContent';
import type { GetPromoTemplateVariantContentQuery } from 'bundles/promo-template/queries/__generated__/getVariantContent';
import getConsumerVariantContent from 'bundles/promo-template/queries/getConsumerVariantContent.graphql';
import getVariantContent from 'bundles/promo-template/queries/getVariantContent.graphql';

export type Modules = Array<
  | PromoTemplateCannedCollectionFragment
  | PromoTemplateValuePropsFragment
  | PromoTemplatePartnerListFragment
  | PromoTemplateCollectionsFragment
  | PromoTemplateTestimonialCarouselFragment
  | PromoTemplateInformationFragment
  | PromoTemplateHeroFragment
  | PromoTemplateArticlesCollectionFragment
  | PromoTemplateFaqsFragment
  | PromoTemplateCitationFragment
  | PromoTemplatePromoBarFragment
>;

export type PromoTemplatePageData = {
  title: string;
  seoDescription?: string;
  seoTitle?: string;
  seoImageUrl?: string;
  seoCanonical?: string;
  collectionsData?: CarouselCollection[];
  preventSearchEngine: boolean;
  blocks: Modules;
};

type PropsFromGraphQL = {
  page?: PromoTemplatePageData;
  loading: boolean;
  error?: ApolloError;
};

const getMergeQueryData = (
  segmentOneData: PromoTemplatePageContentSegmentOneFragment,
  segmentTwoData: PromoTemplatePageContentSegmentTwoFragment
) => {
  const segmentOneDataItems = segmentOneData.modulesCollection?.items ?? [];
  const segmentTwoDataItems = segmentTwoData.modulesCollection?.items ?? [];
  const items = segmentOneDataItems.map((item, index) => (!item?.__typename ? segmentTwoDataItems[index] : item));

  return {
    ...segmentOneData,
    modulesCollection: {
      ...segmentOneData.modulesCollection,
      items,
    },
  };
};

export default function usePageContentQuery(slug: string, domain: string): PropsFromGraphQL {
  const { isPreview } = useContext(PromoTemplateContext);

  // TODO: After full launch of CMS Proxy BFF, remove .get('cmsProxyBffEnabled') check
  const shouldUseCmsProxyBff = GrowthDiscoveryEpicClient.get('cmsProxyBffEnabled') && domain === DOMAINS.explore;

  const segmentOneLocalizedQueryVars = getLocalizedQueryVariables({
    isPreview,
    contentType: 'promoTemplatePageContent',
    fragmentDoc: PromoTemplatePageContentSegmentOneFragmentDoc,
    fragmentFieldsName: 'PromoTemplatePageContentSegmentOne',
    filters: { slug, domain_contains_some: domain },
    contentfulSpace: 'marketing',
  });

  const segmentOneLocalizedVariantQueryVars = {
    ...segmentOneLocalizedQueryVars?.contentfulRequest?.variables,
    query: segmentOneLocalizedQueryVars?.contentfulRequest?.query,
    epicNamespace: 'localization',
    slug,
    contentfulSpace: 'marketing',
  };

  const {
    data: segmentOneData,
    loading: segmentOneLoading,
    error: segmentOneError,
  } = useQuery(getVariantContent, {
    variables: segmentOneLocalizedVariantQueryVars,
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
  });

  const segmentTwoLocalizedQueryVars = getLocalizedQueryVariables({
    isPreview,
    contentType: 'promoTemplatePageContent',
    fragmentDoc: PromoTemplatePageContentSegmentTwoFragmentDoc,
    fragmentFieldsName: 'PromoTemplatePageContentSegmentTwo',
    filters: { slug, domain_contains_some: domain },
    contentfulSpace: 'marketing',
  });

  const segmentTwoLocalizedVariantQueryVars = {
    ...segmentTwoLocalizedQueryVars?.contentfulRequest?.variables,
    query: segmentTwoLocalizedQueryVars?.contentfulRequest?.query,
    epicNamespace: 'localization',
    slug,
    contentfulSpace: 'marketing',
  };

  const {
    data: segmentTwoData,
    loading: segmentTwoLoading,
    error: segmentTwoError,
  } = useQuery(
    // Fetch collection data from CMS Proxy BFF on consumer pages for SEO purposes
    shouldUseCmsProxyBff ? getConsumerVariantContent : getVariantContent,
    {
      variables: segmentTwoLocalizedVariantQueryVars,
      context: { clientName: 'gatewayGql' },
      errorPolicy: 'all',
    }
  );

  if (segmentOneLoading || segmentTwoLoading) {
    return {
      loading: true,
    };
  }

  /**
   * The query is too big for just one call! Contentful recommends
   * splitting large queries into smaller ones in this case.
   * https://www.contentful.com/developers/docs/references/graphql/#/reference/graphql-errors/graphql-errors-explained
   */
  const segmentOneContentData = (segmentOneData as GetPromoTemplateVariantContentQuery)?.LocalizedData
    ?.getVariantContent?.contentDataJson;

  /**
   * (Consumer only) Used to ensure pages with misconfigured collection modules render /
   * fail gracefully instead of 404ing.
   */
  const bffSegmentTwoError =
    shouldUseCmsProxyBff &&
    // Did the BFF return a NOT_FOUND error?
    segmentTwoError?.graphQLErrors?.[0]?.extensions?.errorType === 'NOT_FOUND' &&
    // Does the page contain at least one collection module?
    !!(
      (segmentTwoData as GetConsumerVariantContentQuery)?.LocalizedData?.getVariantContent?.contentDataJson
        ?.modulesCollection as PromoTemplatePageContentModulesCollection
    )?.items?.find((item) => item?.__typename === 'PromoTemplateCollections') &&
    // Do any relatedEntities have falsy entries / empty arrays?
    ((segmentTwoData as GetConsumerVariantContentQuery)?.LocalizedData?.getVariantContent?.relatedEntities?.filter(
      (relatedEntity) => relatedEntity?.entities?.filter(Boolean).length === 0
    ).length ?? 0) > 0;

  let relatedEntities;
  let segmentTwoContentData;
  if (shouldUseCmsProxyBff) {
    const segmentTwo = (segmentTwoData as GetConsumerVariantContentQuery)?.LocalizedData?.getVariantContent;
    if (!bffSegmentTwoError) relatedEntities = segmentTwo?.relatedEntities;
    segmentTwoContentData = segmentTwo?.contentDataJson;
  } else {
    segmentTwoContentData = (segmentTwoData as GetPromoTemplateVariantContentQuery)?.LocalizedData?.getVariantContent
      ?.contentDataJson;
  }

  if (!segmentOneContentData || !segmentTwoContentData) {
    return {
      page: undefined,
      loading: false,
      error: segmentOneError ?? (bffSegmentTwoError ? undefined : segmentTwoError),
    };
  }

  const mergedQueryData = getMergeQueryData(segmentOneContentData, segmentTwoContentData);
  const blocks = (mergedQueryData?.modulesCollection?.items.filter((m) => !!m && m.__typename) ?? []) as Modules;

  const normalizedCollectionsData = relatedEntities
    ?.flatMap((relatedEntity) => {
      return relatedEntity?.entities?.flatMap((entity) => {
        return normalizeCollection(entity as BffCollectionFragment);
      });
    })
    .filter(Boolean) as CarouselCollection[];

  return {
    page: {
      title: mergedQueryData?.title ?? '',
      seoTitle: mergedQueryData?.seoTitle ?? '',
      seoDescription: mergedQueryData?.seoDescription ?? undefined,
      seoImageUrl: mergedQueryData?.seoImage?.url ?? undefined,
      seoCanonical: mergedQueryData?.seoCanonical ?? undefined,
      preventSearchEngine: mergedQueryData?.preventSearchEngine ?? false,
      blocks,
      collectionsData: normalizedCollectionsData,
    },
    loading: false,
    error: segmentOneError ?? (bffSegmentTwoError ? undefined : segmentTwoError),
  };
}
